import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { API, graphqlOperation } from "aws-amplify";
import { GraphQLResult, GraphQLSubscription } from "@aws-amplify/api";
import { getEntities, getEntitiesByDateRange } from "../graphql/queries";
import { createEntity, deleteEntity, updateEntity } from "../graphql/mutations";
import {
  updatedEntity,
  createdEntity,
  deletedEntity,
} from "../graphql/subscriptions";
import {
  CreateEntityInput,
  UpdateEntityInput,
  DeleteEntityMutationVariables,
  GetEntitiesQueryVariables,
  Entity,
  UpdateEntityMutation,
  DeleteEntityMutation,
  CreateEntityMutation,
  GetEntitiesByDateRangeQueryVariables,
  SUCCESS,
  SAFETY,
  BARRIER,
  PROJECT,
  EntityType,
} from "../API";
import {
  StoryTable,
  SafetyTable,
  BarrierTable,
} from "../interfaces/interfaces";

type SubscriptionType = {
  updatedEntity?: Entity | null;
  createdEntity?: Entity | null;
  deletedEntity?: Entity | null;
};

const DataContext = createContext<{
  loading: boolean;
  storyEntities: SUCCESS[];
  safetyEntities: SAFETY[];
  barrierEntities: BARRIER[];
  ideaEntities: PROJECT[];
  allEntities: any;
  setSiteID: React.Dispatch<React.SetStateAction<string | undefined>>;
  setIdeaEntities: React.Dispatch<React.SetStateAction<PROJECT[]>>;
  fetchData: (
    siteId: string,
    shift: string,
    department: string,
    type: string,
    limit?: number,
    nextToken?: string | null
  ) => Promise<{ entities: any[]; nextToken: string | null }>;
  fetchEntitiesByDateRange: (
    siteId: string,
    startDate: string,
    endDate: string,
    limit?: number | null,
    nextToken?: string | null
  ) => Promise<any[]>;
  createEntityMutation: (input: CreateEntityInput) => Promise<boolean>;
  deleteEntityMutation: (
    type: EntityType,
    entityId: string
  ) => Promise<boolean>;
  updateEntityMutation: (
    id: string,
    type: string,
    input: UpdateEntityInput
  ) => Promise<boolean>;
} | null>(null);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};

export const DataProvider: React.FC<{
  children: React.ReactNode;
  user: any;
}> = ({ children, user }) => {
  const [entities, setEntities] = useState<Entity[]>([]);
  const [storyEntities, setStoryEntities] = useState<SUCCESS[]>([]);
  const [safetyEntities, setSafetyEntities] = useState<SAFETY[]>([]);
  const [barrierEntities, setBarrierEntities] = useState<BARRIER[]>([]);
  const [ideaEntities, setIdeaEntities] = useState<PROJECT[]>([]);
  const [allEntities, setAllEntities] = useState<any>([]);

  const [loading, setLoading] = useState(true);
  const [siteId, setSiteIDState] = useState<string | undefined>(undefined);
  const entitiesRef = useRef(entities);
  // Only the newest date-range request may update shared report data. This
  // prevents a slower, older request from replacing a newer result.
  const dateRangeRequestIdRef = useRef(0);

  useEffect(() => {
    entitiesRef.current = entities;
  }, [entities]);

  useEffect(() => {
    const localSiteId = localStorage.getItem("localSite");
    if (!siteId && localSiteId) {
      setSiteIDState(localSiteId);
    }
  }, [siteId]);

  const setSiteID = useCallback(
    (value: React.SetStateAction<string | undefined>) => {
      setSiteIDState(value);
    },
    []
  );
  const fetchEntitiesByDateRange = useCallback(
    async (
      siteId: string,
      startDate: string,
      endDate: string,
      limit?: number | null,
      nextToken?: string | null
    ) => {
      const requestId = ++dateRangeRequestIdRef.current;
      try {
        let allFetchedEntities: any[] = [];
        let currentNextToken = nextToken;

        do {
          const result: GraphQLResult<any> = await API.graphql(
            graphqlOperation(getEntitiesByDateRange, {
              siteId,
              startDate,
              endDate,
              limit,
              nextToken: currentNextToken,
            } as GetEntitiesByDateRangeQueryVariables)
          );

          const response = result?.data?.getEntitiesByDateRange;
          if (response?.success === false) {
            throw new Error(response.message || "Failed to fetch entities by date range");
          }
          const fetchedEntities = response?.entities || [];
          allFetchedEntities = [...allFetchedEntities, ...fetchedEntities];

          // Get the next token for the next page
          currentNextToken = response?.nextToken;

          console.log("Fetched batch of entities:", fetchedEntities.length);
        } while (currentNextToken); // Continue fetching while there's a next token

        console.log("Total entities fetched:", allFetchedEntities);
        if (requestId === dateRangeRequestIdRef.current) {
          setAllEntities(allFetchedEntities);
        }
        return allFetchedEntities;
      } catch (error) {
        console.error("Error fetching entities by date range:", error);
        if (requestId === dateRangeRequestIdRef.current) {
          setAllEntities([]);
        }
        throw error;
      } finally {
        if (requestId === dateRangeRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    []
  );

  const fetchData = useCallback(
    async (
      siteId: string,
      shift: string,
      department: string,
      type: string,
      limit: number = 50,
      nextToken?: string | null
    ) => {
      try {
        const result: GraphQLResult<any> = await API.graphql(
          graphqlOperation(getEntities, {
            siteId,
            shift,
            department,
            type,
            limit,
            nextToken,
          } as GetEntitiesQueryVariables)
        );
        
        const responseNextToken = result?.data?.getEntities?.nextToken || null;
        let entities: any[] = [];

        if (type === "SUCCESS") {
          entities = result?.data?.getEntities.entities
            ?.filter((entity: { type: string }) => entity.type === "SUCCESS")
            ?.map((entity: SUCCESS) => entity as StoryTable)
            ?.sort(
              (a: StoryTable, b: StoryTable) =>
                new Date(b.date).getTime() - new Date(a.date).getTime()
            ) || [];
          
          if (!nextToken) setStoryEntities(entities);
        } else if (type === "SAFETY") {
          entities = result?.data?.getEntities.entities
            ?.filter((entity: { type: string }) => entity.type === "SAFETY")
            ?.map((entity: SAFETY) => entity as SafetyTable)
            ?.sort(
              (a: SafetyTable, b: SafetyTable) =>
                new Date(b.date).getTime() - new Date(a.date).getTime()
            ) || [];
          
          if (!nextToken) setSafetyEntities(entities);
        } else if (type === "BARRIER") {
          entities = result?.data?.getEntities?.entities
            ?.filter((entity: { type: string }) => entity?.type === "BARRIER")
            ?.map(
              (entity: BARRIER) =>
                ({
                  ...entity,
                  priority: entity.priority?.toString() || "5",
                } as BarrierTable)
            )
            ?.sort(
              (a: BarrierTable, b: BarrierTable) =>
                new Date(b.date).getTime() - new Date(a.date).getTime()
            ) || [];
          
          if (!nextToken) setBarrierEntities(entities);
        } else if (type === "PROJECT") {
          entities = result?.data?.getEntities.entities
            ?.filter((entity: { type: string }) => entity.type === "PROJECT")
            ?.map((entity: PROJECT) => entity as PROJECT)
            ?.sort(
              (a: PROJECT, b: PROJECT) =>
                new Date(b.date).getTime() - new Date(a.date).getTime()
            ) || [];
          
          if (!nextToken) {
            setIdeaEntities(prevEntities => {
              const combined = [...prevEntities, ...entities];
              const unique = combined.filter((entity, index, self) => 
                index === self.findIndex(e => e.id === entity.id)
              );
              return unique.sort(
                (a: PROJECT, b: PROJECT) =>
                  new Date(b.date).getTime() - new Date(a.date).getTime()
              );
            });
          }
        }

        return {
          entities,
          nextToken: responseNextToken,
        };
      } catch (error) {
        console.error("Error fetching data:", error);
        return { entities: [], nextToken: null };
      } finally {
        if (!nextToken) setLoading(false);
      }
    },
    []
  );

  const createEntityMutation = async (
    input: CreateEntityInput
  ): Promise<boolean> => {
    try {
      console.log("createEntityMutation", input);
      console.log("siteId", siteId);

      if (!siteId) {
        console.error("siteId is not set");
        return false;
      }

      const response = (await API.graphql(
        graphqlOperation(createEntity, { siteId, input })
      )) as GraphQLResult<CreateEntityMutation>;

      console.log("GraphQL response", response);

      if (response.errors) {
        console.error("GraphQL errors:", response.errors);
        return false;
      }

      if (!response.data?.createEntity) {
        console.error("No data returned from createEntity mutation");
        return false;
      }
      // Add the newly created entity to storyEntities
      const newEntity = response.data.createEntity.entity;

      console.log("new before", response.data.createEntity);
      console.log("newEntity before", newEntity);

      if (newEntity?.type === EntityType.SUCCESS) {
        // Sort the storyEntities by date after adding the new entity
        setStoryEntities((prevStoryEntities) =>
          [...prevStoryEntities, newEntity as SUCCESS].sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
          )
        );
        console.log("storyEntities", storyEntities);
      } else if (newEntity?.type === EntityType.SAFETY) {
        // Add the newly created entity to safetyEntities if it is of type SAFETY
        setSafetyEntities((prevSafetyEntities) =>
          [...prevSafetyEntities, newEntity as SAFETY].sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
          )
        );
        console.log("safetyEntites", safetyEntities);
      } else if (newEntity?.type === EntityType.PROJECT) {
        // Add the newly created entity to safetyEntities if it is of type PROJECT
        setIdeaEntities((prevIdeaEntities) =>
          [...prevIdeaEntities, newEntity as PROJECT].sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
          )
        );
        console.log("ideaEntites", ideaEntities);
      } else if (newEntity?.type === EntityType.BARRIER) {
        setBarrierEntities((prevBarrierEntities) =>
          [...prevBarrierEntities, newEntity as BARRIER].sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
          )
        );
        console.log("barrierEntities", barrierEntities);
      }

      return response.data.createEntity.success ?? false;
    } catch (error) {
      console.error("Error creating entity:", error);
      throw error;
    }
  };
  // useEffect(() => {
  //   console.log("Updated safetyEntities:", safetyEntities);
  // }, [safetyEntities]);
  const deleteEntityMutation = async (
    type: EntityType,
    entityId: string
  ): Promise<boolean> => {
    console.log("deleteEntityMutation", entityId);
    try {
      const response = (await API.graphql(
        graphqlOperation(deleteEntity, {
          siteId: siteId,
          type: type,
          id: entityId,
        } as unknown as DeleteEntityMutationVariables)
      )) as GraphQLResult<DeleteEntityMutation>;
      console.log("response", response);
      const deletedEntity = response.data?.deleteEntity.id;
      if (deletedEntity) {
        setStoryEntities((prevStoryEntities) =>
          prevStoryEntities.filter((entity) => entity.id !== deletedEntity)
        );
        setSafetyEntities((prevSafetyEntities) =>
          prevSafetyEntities.filter((entity) => entity.id !== deletedEntity)
        );
        setBarrierEntities((prevBarrierEntities) =>
          prevBarrierEntities.filter((entity) => entity.id !== deletedEntity)
        );
        setIdeaEntities((prevIdeaEntities) =>
          prevIdeaEntities.filter((entity) => entity.id !== deletedEntity)
        );
      }
      return response.data?.deleteEntity.success ?? false;
    } catch (error) {
      console.error("Error deleting entity:", error);
      throw error;
    }
  };

  const updateEntityMutation = async (
    id: string,
    type: string,
    input: UpdateEntityInput
  ): Promise<boolean> => {
    try {
      const response = (await API.graphql(
        graphqlOperation(updateEntity, { siteId, id, type, input })
      )) as GraphQLResult<UpdateEntityMutation>;

      if (response.errors) {
        console.error("GraphQL errors:", response.errors);
        return false;
      }

      if (!response.data?.updateEntity) {
        console.error("No data returned from updateEntity mutation");
        return false;
      }

      const updatedEntity = response.data.updateEntity.entity;

      // Update the storyEntities with the updated entity
      if (updatedEntity?.type === EntityType.SUCCESS) {
        setStoryEntities((prevStoryEntities) =>
          prevStoryEntities
            .map((entity) =>
              entity.id === updatedEntity.id
                ? (updatedEntity as SUCCESS)
                : entity
            )
            .sort(
              (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
            )
        );
      } else if (updatedEntity?.type === EntityType.SAFETY) {
        setSafetyEntities((prevSafetyEntities) =>
          prevSafetyEntities
            .map((entity) =>
              entity.id === updatedEntity.id
                ? (updatedEntity as SAFETY)
                : entity
            )
            .sort(
              (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
            )
        );
      } else if (updatedEntity?.type === EntityType.BARRIER) {
        setBarrierEntities((prevBarrierEntities) =>
          prevBarrierEntities
            .map((entity) =>
              entity.id === updatedEntity.id
                ? (updatedEntity as BARRIER)
                : entity
            )
            .sort(
              (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
            )
        );
      } else if (updatedEntity?.type === EntityType.PROJECT) {
        setIdeaEntities((prevIdeaEntities) =>
          prevIdeaEntities
            .map((entity) =>
              entity.id === updatedEntity.id
                ? (updatedEntity as PROJECT)
                : entity
            )
            .sort(
              (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
            )
        );
      }

      return response.data.updateEntity.success ?? false;
    } catch (error) {
      console.error("Error updating entity:", error);
      throw error;
    }
  };

  const handleSubscriptionMessage = (value: any) => {
    if (value.data) {
      try {
        const message = value.data;

        if (message.updatedEntity) {
          if (message.updatedEntity.entity.entityId === undefined) {
            console.log("Entity ID is undefined.");
            return;
          }
          const existingEntityIndex = entitiesRef.current.findIndex(
            (entity) => entity.id === message.updatedEntity.entity.entityId
          );
          if (existingEntityIndex !== -1) {
            setEntities((prevEntities) => {
              const updatedEntities = [...prevEntities];
              updatedEntities[existingEntityIndex] =
                message.updatedEntity.entity;
              return updatedEntities;
            });
          }
        }

        if (message.createdEntity) {
          if (message.createdEntity.entity.entityId === undefined) {
            return;
          }
          const existingEntity = entitiesRef.current.find(
            (entity) => entity.id === message.createdEntity.entity.entityId
          );
          if (!existingEntity) {
            setEntities((prevEntities) => [
              ...prevEntities,
              message.createdEntity.entity,
            ]);
          }
        }

        if (message.deletedEntity) {
          if (message.deletedEntity.entityId === undefined) return;
          setEntities((prevEntities) =>
            prevEntities.filter(
              (entity) => entity.id !== message.deletedEntity.entityId
            )
          );
        }
      } catch (error) {
        console.error("Error handling subscription message:", error);
      }
    }
  };

  const useGraphQLSubscription = (operation: any, siteId: any, user: any) => {
    useEffect(() => {
      const subscription = API.graphql<GraphQLSubscription<SubscriptionType>>(
        graphqlOperation(operation, { siteId })
      ).subscribe({
        next: handleSubscriptionMessage,
        error: (error) => console.warn(error),
      });

      return () => subscription.unsubscribe();
    }, [operation, siteId, user]);
  };

  // useGraphQLSubscription(updatedEntity, siteId, user);
  // useGraphQLSubscription(createdEntity, siteId, user);
  // useGraphQLSubscription(deletedEntity, siteId, user);

  const value = {
    loading,
    storyEntities,
    safetyEntities,
    barrierEntities,
    ideaEntities,
    allEntities,
    setSiteID,
    setIdeaEntities,
    fetchData,
    createEntityMutation,
    deleteEntityMutation,
    updateEntityMutation,
    fetchEntitiesByDateRange,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
